import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Alert, Spinner, Badge } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import './coldemailer.css';
import './coldemailer-styles.css';

// Every column on Company, grouped so the page reads in a sensible order rather than model order.
// Each field names how it is rendered, since the table mixes plain text, dates, urls and columns
// holding a JSON array as a string.
//
// Kinds:
//   text   - shown as is
//   long   - scraped prose, given its own full width block
//   url    - rendered as a link
//   email  - rendered as a mailto link
//   image  - rendered as an image, falling back to the url when it will not load
//   date   - formatted
//   list   - a JSON array held as a string, or a plain string, rendered as tags
//   object - a JSON object held as a string, rendered as its key and value pairs
//   status - rendered as a badge
const c_FieldGroups = [
	{
		title: 'Identity',
		fields: [
			{ key: 'name', label: 'Name', kind: 'text' },
			{ key: 'legal_name', label: 'Legal Name', kind: 'text' },
			{ key: 'legal_entity', label: 'Legal Entity', kind: 'text' },
			{ key: 'url', label: 'Url', kind: 'url' },
			{ key: 'email', label: 'Email', kind: 'email' },
			{ key: 'phone_numbers', label: 'Phone Numbers', kind: 'list' },
			{ key: 'logo', label: 'Logo', kind: 'image' }
		]
	},
	{
		title: 'Classification',
		fields: [
			{ key: 'niche', label: 'Niche', kind: 'text' },
			{ key: 'industry', label: 'Industry', kind: 'long' },
			{ key: 'services', label: 'Services', kind: 'list' },
			{ key: 'keywords', label: 'Keywords', kind: 'list' },
			{ key: 'technographics', label: 'Technographics', kind: 'list' },
			{ key: 'social_media', label: 'Social Media', kind: 'list' }
		]
	},
	{
		title: 'Business',
		fields: [
			{ key: 'annual_revenue', label: 'Annual Revenue', kind: 'text' },
			{ key: 'num_employees', label: 'Employees', kind: 'text' },
			{ key: 'years_in_business', label: 'Years In Business', kind: 'text' },
			{ key: 'bbb_rating', label: 'BBB Rating', kind: 'text' },
			{ key: 'bbb_accredited_since', label: 'BBB Accredited Since', kind: 'date' },
			{ key: 'business_started', label: 'Business Started', kind: 'date' },
			{ key: 'business_incorporated', label: 'Business Incorporated', kind: 'date' },
			{ key: 'local_coc_member', label: 'Local CoC Member', kind: 'boolean' },
			{ key: 'opening_hours', label: 'Opening Hours', kind: 'object' }
		]
	},
	{
		title: 'Status',
		fields: [
			{ key: 'enrichment_status', label: 'Enrichment Status', kind: 'status' },
			{ key: 'data_status', label: 'Data Status', kind: 'status' },
			{ key: 'qualification_status', label: 'Qualification Status', kind: 'status' },
			{ key: 'priority', label: 'Priority', kind: 'text' },
			{ key: 'discovered_at', label: 'Discovered', kind: 'date' },
			{ key: 'last_checked_at', label: 'Last Checked', kind: 'date' },
			{ key: 'createdAt', label: 'Created', kind: 'date' },
			{ key: 'updatedAt', label: 'Updated', kind: 'date' }
		]
	},
	{
		title: 'Discovery',
		fields: [
			{ key: 'search_query', label: 'Search Query', kind: 'long' },
			{ key: 'search_type', label: 'Search Type', kind: 'text' }
		]
	},
	{
		title: 'Notes',
		fields: [
			{ key: 'description', label: 'Description', kind: 'long' },
			{ key: 'notes', label: 'Notes', kind: 'long' }
		]
	},
	{
		title: 'References',
		fields: [
			{ key: 'id', label: 'Id', kind: 'text' },
			{ key: 'main_contact_id', label: 'Main Contact Id', kind: 'text' },
			{ key: 'address_id', label: 'Address Id', kind: 'text' },
			{ key: 'province_id', label: 'Province Id', kind: 'text' },
			{ key: 'city_id', label: 'City Id', kind: 'text' },
			{ key: 'municipality_id', label: 'Municipality Id', kind: 'text' },
			{ key: 'niche_id', label: 'Niche Id', kind: 'text' }
		]
	}
];

const c_StatusVariants = {
	Enriched: 'success',
	Enriching: 'info',
	Pending: 'warning',
	Failed: 'danger',
	Paused: 'secondary',
	Resumed: 'info',
	Scraping: 'info',
	Scraped: 'primary',
	BadData: 'danger',
	Cleaned: 'success',
	Duplicate: 'secondary',
	Blacklisted: 'danger',
	Qualified: 'success',
	NotQualified: 'danger',
	NotAnalyzed: 'secondary'
};

const format_date = (value) => {
	if(!value) {
		return '';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Several columns hold a JSON array that was stored as a string, while others hold a plain string
// for the same kind of data. Both shapes are turned into an array here so one renderer covers them,
// matching how the lead enrichment report reads the same columns.
const to_value_list = (value) => {
	if(!value) {
		return [];
	}

	if(Array.isArray(value)) {
		return value;
	}

	const text = String(value).trim();
	if(!text) {
		return [];
	}

	if(text.indexOf('[') === 0) {
		try {
			const parsed = JSON.parse(text);
			if(Array.isArray(parsed)) {
				return parsed;
			}
		} catch (error) {
			console.log('CompanyView.to_value_list - ERROR: ', error);
		}
	}

	return [text];
};

// A column holding a JSON object as a string, turned into name and value pairs. Returns null when
// the text is not an object, so the caller can fall back to showing it as it was stored.
const to_value_pairs = (value) => {
	if(!value) {
		return null;
	}

	var parsed = value;

	if(typeof parsed === 'string') {
		const text = parsed.trim();
		if(text.indexOf('{') !== 0) {
			return null;
		}

		try {
			parsed = JSON.parse(text);
		} catch (error) {
			console.log('CompanyView.to_value_pairs - ERROR: ', error);
			return null;
		}
	}

	if(!parsed || (typeof parsed !== 'object') || Array.isArray(parsed)) {
		return null;
	}

	const names = Object.keys(parsed);
	if(!names.length) {
		return null;
	}

	return names.map((name) => {
		return { name, value: parsed[name] };
	});
};

// The keys come from the scraper, so they arrive as hours_weekdays rather than anything a person
// would want to read. Underscores become spaces and each word is capitalised.
const to_readable_name = (name) => {
	const words = String(name).split('_');

	return words.map((word) => {
		if(!word) {
			return word;
		}

		return word.charAt(0).toUpperCase() + word.slice(1);
	}).join(' ');
};

// Scraped addresses arrive carrying the markup they sat in on the page, so an address lifted from
// bold text is stored as **name@example.com**. The characters are stripped for display and for the
// mailto link, which a stray asterisk or quote would otherwise break. The stored value is left
// alone: this view does not write.
const clean_email = (value) => {
	return String(value).replace(/[*"'`]/g, '').trim();
};

const get_status_variant = (value) => {
	if(value && c_StatusVariants[value]) {
		return c_StatusVariants[value];
	}

	return 'secondary';
};

// A column with nothing in it is still shown, so the page always reflects the whole row rather
// than silently hiding the fields that happen to be empty for this company
const is_empty_value = (value) => {
	return (value === null) || (value === undefined) || (value === '');
};

function CompanyView() {
	const navigate = useNavigate();
	const { id } = useParams();

	const [company, set_company] = useState(null);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	const [logo_failed, set_logo_failed] = useState(false);

	const load_company = async () => {
		set_loading(true);
		set_error('');

		try {
			const result = await coldemailer_api.get_company(id);
			set_company(result);
		} catch (error) {
			console.log('CompanyView.load_company - ERROR: ', error);
			set_error('Failed to load the company.');
		} finally {
			set_loading(false);
		}
	};

	useEffect(function() {
		set_logo_failed(false);
		load_company();
	}, [id]);

	const on_back = () => {
		navigate(coldemailer_config.lead_discovery_route);
	};

	const on_logo_error = () => {
		set_logo_failed(true);
	};

	const render_list_value = (value) => {
		const values = to_value_list(value);

		if(!values.length) {
			return <span className="ce-detail-empty">-</span>;
		}

		return (
			<div className="ce-tag-list">
				{values.map(function(item, item_index) {
					return (
						<span key={item_index} className="ce-tag">
							{String(item)}
						</span>
					);
				})}
			</div>
		);
	};

	const render_object_value = (value) => {
		const pairs = to_value_pairs(value);

		// Not an object after all, so it is shown as stored rather than hidden
		if(!pairs) {
			return String(value);
		}

		return (
			<div className="ce-pair-list">
				{pairs.map(function(pair) {
					return (
						<div key={pair.name} className="ce-pair">
							<span className="ce-pair-name">{to_readable_name(pair.name)}</span>
							<span className="ce-pair-value">{String(pair.value)}</span>
						</div>
					);
				})}
			</div>
		);
	};

	const render_url_value = (value) => {
		return (
			<a href={value} target="_blank" rel="noopener noreferrer" className="ce-link">
				{value}
			</a>
		);
	};

	const render_email_value = (value) => {
		const email = clean_email(value);

		return (
			<a href={'mailto:' + email} className="ce-link">
				{email}
			</a>
		);
	};

	// Falls back to showing the address when the image will not load, so a broken logo url is
	// still readable rather than an empty box
	const render_image_value = (value) => {
		if(logo_failed) {
			return render_url_value(value);
		}

		return (
			<img
				src={value}
				alt="Logo"
				className="ce-detail-logo"
				onError={on_logo_error}
			/>
		);
	};

	const render_field_value = (field) => {
		const value = company[field.key];

		if(is_empty_value(value)) {
			return <span className="ce-detail-empty">-</span>;
		}

		if(field.kind === 'list') {
			return render_list_value(value);
		}

		if(field.kind === 'object') {
			return render_object_value(value);
		}

		if(field.kind === 'url') {
			return render_url_value(value);
		}

		if(field.kind === 'email') {
			return render_email_value(value);
		}

		if(field.kind === 'image') {
			return render_image_value(value);
		}

		if(field.kind === 'date') {
			const formatted = format_date(value);
			if(!formatted) {
				return <span className="ce-detail-empty">-</span>;
			}

			return formatted;
		}

		if(field.kind === 'status') {
			return (
				<Badge bg={get_status_variant(value)}>
					{value}
				</Badge>
			);
		}

		if(field.kind === 'boolean') {
			return value ? 'Yes' : 'No';
		}

		return String(value);
	};

	const render_field = (field) => {
		// Both kinds render a block rather than a single line, so they take the full width. A pair
		// list is short, so it also carries a class that lifts the scroll cap meant for prose.
		const is_object = (field.kind === 'object');
		const is_long = (field.kind === 'long') || is_object;

		var row_class = 'ce-detail-row';
		if(is_long) {
			row_class += ' ce-detail-row-long';
		}

		if(is_object) {
			row_class += ' ce-detail-row-pairs';
		}

		return (
			<div key={field.key} className={row_class}>
				<div className="ce-detail-label">{field.label}</div>
				<div className="ce-detail-value">{render_field_value(field)}</div>
			</div>
		);
	};

	const render_group = (group) => {
		return (
			<div key={group.title} className="ce-panel ce-detail-panel">
				<h5 className="ce-panel-title">{group.title}</h5>

				<div className="ce-detail-grid">
					{group.fields.map(render_field)}
				</div>
			</div>
		);
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div>
					<h4 className="ce-title">
						{company && company.name ? company.name : 'Company'}
					</h4>
					<p className="ce-subtitle">Lead Discovery company record</p>
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={on_back}>
						Back
					</Button>
				</div>
			</div>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_error = () => {
		return (
			<Alert variant="danger">
				{error}
			</Alert>
		);
	};

	const render_body = () => {
		if(loading) {
			return render_loading();
		}

		if(!company) {
			return null;
		}

		return (
			<React.Fragment>
				{c_FieldGroups.map(render_group)}
			</React.Fragment>
		);
	};

	// Set as a custom property so the host app's header height stays configurable
	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root" style={root_style}>
			{render_header()}

			{error ? render_error() : null}

			{render_body()}
		</div>
	);
}

export default CompanyView;
